import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useBranding, useDeviceLayout } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  Shield, FileText, Users, Share2, Activity, LogOut, Menu, X,
  Home, Settings, FolderLock, Clock, Upload, TrendingUp, UserPlus,
  CalendarDays, CheckSquare, UserCheck, Search, LayoutTemplate
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = ({ children, title }) => {
  const { user, logout, token } = useAuth();
  const { appName, logoUrl, backgroundLogoUrl, backgroundLogoOpacity, backgroundLogoSize, backgroundLogoPosition, headingFont } = useBranding();
  const { config, isMobile } = useDeviceLayout();
  const { t } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const sidebarVisible = !isMobile && config.sidebar_visible !== false;
  const sidebarCollapsed = sidebarVisible && config.sidebar_collapsed === true;
  const showBottomNav = isMobile && config.bottom_nav !== false;
  const showHeaderSearch = config.show_header_search !== false;

  const navigation = [
    { name: t('nav.dashboard'), href: '/dashboard', icon: Home },
    { name: t('nav.myDocuments'), href: '/documents', icon: FolderLock },
    { name: t('nav.sharedWithMe'), href: '/shared', icon: Share2 },
    { name: t('calendar.title'), href: '/calendar', icon: CalendarDays },
    { name: t('tasks.title'), href: '/tasks', icon: CheckSquare },
  ];

  const managerNavigation = [
    { name: 'Templates', href: '/templates', icon: LayoutTemplate },
  ];

  const adminNavigation = [
    { name: t('nav.userManagement'), href: '/users', icon: Users },
    { name: 'Invite Users', href: '/invites', icon: UserPlus },
    { name: 'Pending Approvals', href: '/pending-users', icon: UserCheck },
    { name: 'Registration Docs', href: '/registration-documents', icon: FileText },
    { name: t('nav.auditLogs'), href: '/audit-logs', icon: Activity },
  ];

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isManager = isAdmin || user?.role === 'manager';
  const sidebarWidth = sidebarCollapsed ? 'w-[72px]' : 'w-64';
  const mainPadding = sidebarVisible ? (sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64') : '';

  const bottomNavItems = [
    { name: 'Home', href: '/dashboard', icon: Home },
    { name: 'Docs', href: '/documents', icon: FolderLock },
    { name: 'Calendar', href: '/calendar', icon: CalendarDays },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  return (
    <div className={`min-h-screen bg-[#050505] ${showBottomNav ? 'pb-16' : ''}`}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop (when visible) + Mobile slide-out */}
      {(sidebarVisible || sidebarOpen) && (
        <aside className={`fixed top-0 left-0 z-50 h-full ${sidebarWidth} bg-[#0A0A0A] border-r border-white/10 transform transition-all duration-300 ${sidebarVisible ? 'lg:translate-x-0' : ''} ${sidebarOpen ? 'translate-x-0 !w-64' : '-translate-x-full lg:translate-x-0'}`}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className={`flex items-center justify-between border-b border-white/10 ${sidebarCollapsed && !sidebarOpen ? 'p-3 justify-center' : 'p-6'}`}>
              <Link to="/dashboard" className="flex items-center gap-2" title={appName}>
                {logoUrl ? (
                  <img src={logoUrl} alt={appName} className={`${sidebarCollapsed && !sidebarOpen ? 'h-7 w-auto' : 'h-8 w-auto'}`} />
                ) : (
                  <Shield className="w-8 h-8 text-primary flex-shrink-0" />
                )}
                {(!sidebarCollapsed || sidebarOpen) && (
                  <span className="font-bold text-xl text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {appName}
                  </span>
                )}
              </Link>
              <button 
                className="lg:hidden text-white/60 hover:text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User Info */}
            {(!sidebarCollapsed || sidebarOpen) && (
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-white/40 font-mono uppercase tracking-wider">
                      {user?.role?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {sidebarCollapsed && !sidebarOpen && (
              <div className="py-3 flex justify-center border-b border-white/10">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-semibold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
              {(!sidebarCollapsed || sidebarOpen) && <p className="text-xs font-mono uppercase tracking-widest text-white/30 mb-3 px-3">Main</p>}
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={sidebarCollapsed && !sidebarOpen ? item.name : undefined}
                    className={`flex items-center gap-3 ${sidebarCollapsed && !sidebarOpen ? 'justify-center px-2' : 'px-3'} py-2 rounded-sm transition-colors ${
                      isActive 
                        ? 'bg-primary/10 text-primary border border-primary/30' 
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {(!sidebarCollapsed || sidebarOpen) && <span className="text-sm">{item.name}</span>}
                  </Link>
                );
              })}

              {isManager && (
                <>
                  {(!sidebarCollapsed || sidebarOpen) && <p className="text-xs font-mono uppercase tracking-widest text-white/30 mb-3 mt-6 px-3">Manage</p>}
                  {sidebarCollapsed && !sidebarOpen && <div className="border-t border-white/10 my-2" />}
                  {managerNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        title={sidebarCollapsed && !sidebarOpen ? item.name : undefined}
                        className={`flex items-center gap-3 ${sidebarCollapsed && !sidebarOpen ? 'justify-center px-2' : 'px-3'} py-2 rounded-sm transition-colors ${
                          isActive 
                            ? 'bg-primary/10 text-primary border border-primary/30' 
                            : 'text-white/60 hover:text-white hover:bg-white/5'
                        }`}
                        data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {(!sidebarCollapsed || sidebarOpen) && <span className="text-sm">{item.name}</span>}
                      </Link>
                    );
                  })}
                </>
              )}

              {isAdmin && (
                <>
                  {(!sidebarCollapsed || sidebarOpen) && <p className="text-xs font-mono uppercase tracking-widest text-white/30 mb-3 mt-6 px-3">Admin</p>}
                  {sidebarCollapsed && !sidebarOpen && <div className="border-t border-white/10 my-2" />}
                  {adminNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        title={sidebarCollapsed && !sidebarOpen ? item.name : undefined}
                        className={`flex items-center gap-3 ${sidebarCollapsed && !sidebarOpen ? 'justify-center px-2' : 'px-3'} py-2 rounded-sm transition-colors ${
                          isActive 
                            ? 'bg-primary/10 text-primary border border-primary/30' 
                            : 'text-white/60 hover:text-white hover:bg-white/5'
                        }`}
                        data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {(!sidebarCollapsed || sidebarOpen) && <span className="text-sm">{item.name}</span>}
                      </Link>
                    );
                  })}
                </>
              )}
            </nav>

            {/* Bottom Actions */}
            <div className="p-2 border-t border-white/10 space-y-1">
              <Link
                to="/settings"
                title={sidebarCollapsed && !sidebarOpen ? t('nav.settings') : undefined}
                className={`flex items-center gap-3 ${sidebarCollapsed && !sidebarOpen ? 'justify-center px-2' : 'px-3'} py-2 rounded-sm transition-colors ${
                  location.pathname === '/settings'
                    ? 'bg-primary/10 text-primary border border-primary/30'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid="nav-settings"
              >
                <Settings className="w-5 h-5 flex-shrink-0" />
                {(!sidebarCollapsed || sidebarOpen) && <span className="text-sm">{t('nav.settings')}</span>}
              </Link>
              <button
                onClick={logout}
                title={sidebarCollapsed && !sidebarOpen ? t('nav.logout') : undefined}
                className={`w-full flex items-center gap-3 ${sidebarCollapsed && !sidebarOpen ? 'justify-center px-2' : 'px-3'} py-2 rounded-sm text-red-400 hover:bg-red-400/10 transition-colors`}
                data-testid="nav-logout"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                {(!sidebarCollapsed || sidebarOpen) && <span className="text-sm">{t('nav.logout')}</span>}
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* No desktop sidebar - still need mobile slide-out */}
      {!sidebarVisible && sidebarOpen && null}

      {/* Main Content */}
      <div className={mainPadding}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              {(!sidebarVisible || isMobile) && (
                <button
                  className="text-white/60 hover:text-white"
                  onClick={() => setSidebarOpen(true)}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-6 h-6" />
                </button>
              )}
              <h1 className="text-xl font-semibold text-white" style={{ fontFamily: headingFont ? `'${headingFont}', sans-serif` : 'Space Grotesk, sans-serif' }}>
                {title}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {showHeaderSearch && (
                <div className="hidden sm:flex items-center gap-2 bg-white/5 border border-white/10 rounded-sm px-3 py-1.5" data-testid="header-search-bar">
                  <Search className="w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="bg-transparent text-white text-sm placeholder:text-white/30 w-32 lg:w-48 outline-none"
                    data-testid="header-search-input"
                  />
                </div>
              )}
              <NotificationBell />
              <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded-sm">
                ENCRYPTED
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 relative">
          {backgroundLogoUrl && (
            <div 
              className="fixed pointer-events-none z-0"
              style={{
                opacity: backgroundLogoOpacity || 0.05,
                width: backgroundLogoSize || '400px',
                height: backgroundLogoSize || '400px',
                top: '50%',
                left: sidebarVisible ? `calc(50% + ${sidebarCollapsed ? '36px' : '128px'})` : '50%',
                transform: 'translate(-50%, -50%)',
              }}
              data-testid="dashboard-background-logo"
            >
              <img src={backgroundLogoUrl} alt="" className="w-full h-full object-contain" />
            </div>
          )}
          <div className="relative z-10" style={{ maxWidth: config.content_max_width || 'none' }}>
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      {showBottomNav && (
        <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A] border-t border-white/10 flex items-center justify-around py-2 px-1" data-testid="mobile-bottom-nav">
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-sm transition-colors ${isActive ? 'text-primary' : 'text-white/40'}`}
                data-testid={`bottom-nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const { config } = useDeviceLayout();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const showStatsBar = config.show_stats_bar !== false;
  const showQuickActions = config.show_quick_actions !== false;
  const showRecentActivity = config.show_recent_activity !== false;
  const dashboardCols = config.dashboard_columns || 4;
  const cardStyle = config.card_style || 'default';

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stats/dashboard`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return '🔐';
      case 'register': return '📝';
      case 'document_upload': return '📤';
      case 'document_download': return '📥';
      case 'document_delete': return '🗑️';
      case 'access_grant': return '✅';
      case 'access_revoke': return '❌';
      default: return '📋';
    }
  };

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm p-6">
          <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h2>
          <p className="text-white/60">
            Your documents are encrypted and secure. Here's your vault overview.
          </p>
        </div>

        {/* Stats Grid */}
        {showStatsBar && (
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(dashboardCols, 4)}, minmax(0, 1fr))` }} data-testid="stats-grid">
          <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm card-hover ${cardStyle === 'compact' ? 'p-4' : cardStyle === 'minimal' ? 'p-3 border-0 bg-white/[0.03]' : 'p-6'}`} data-testid="stat-my-documents">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-mono uppercase tracking-widest text-white/40 mb-1 ${cardStyle === 'compact' ? 'text-[10px]' : 'text-xs'}`}>My Documents</p>
                <p className={`font-bold text-white ${cardStyle === 'compact' ? 'text-2xl' : cardStyle === 'minimal' ? 'text-xl' : 'text-3xl'}`}>{loading ? '...' : stats?.my_documents || 0}</p>
              </div>
              {cardStyle !== 'minimal' && (
                <div className={`rounded-sm bg-primary/10 flex items-center justify-center ${cardStyle === 'compact' ? 'w-10 h-10' : 'w-12 h-12'}`}>
                  <FileText className={`text-primary ${cardStyle === 'compact' ? 'w-5 h-5' : 'w-6 h-6'}`} />
                </div>
              )}
            </div>
          </div>

          <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm card-hover ${cardStyle === 'compact' ? 'p-4' : cardStyle === 'minimal' ? 'p-3 border-0 bg-white/[0.03]' : 'p-6'}`} data-testid="stat-shared-with-me">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-mono uppercase tracking-widest text-white/40 mb-1 ${cardStyle === 'compact' ? 'text-[10px]' : 'text-xs'}`}>Shared With Me</p>
                <p className={`font-bold text-white ${cardStyle === 'compact' ? 'text-2xl' : cardStyle === 'minimal' ? 'text-xl' : 'text-3xl'}`}>{loading ? '...' : stats?.shared_with_me || 0}</p>
              </div>
              {cardStyle !== 'minimal' && (
                <div className={`rounded-sm bg-secondary/10 flex items-center justify-center ${cardStyle === 'compact' ? 'w-10 h-10' : 'w-12 h-12'}`}>
                  <Share2 className={`text-secondary ${cardStyle === 'compact' ? 'w-5 h-5' : 'w-6 h-6'}`} />
                </div>
              )}
            </div>
          </div>

          {(user?.role === 'super_admin' || user?.role === 'admin') && (
            <>
              <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm card-hover ${cardStyle === 'compact' ? 'p-4' : cardStyle === 'minimal' ? 'p-3 border-0 bg-white/[0.03]' : 'p-6'}`} data-testid="stat-total-users">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-mono uppercase tracking-widest text-white/40 mb-1 ${cardStyle === 'compact' ? 'text-[10px]' : 'text-xs'}`}>Total Users</p>
                    <p className={`font-bold text-white ${cardStyle === 'compact' ? 'text-2xl' : cardStyle === 'minimal' ? 'text-xl' : 'text-3xl'}`}>{loading ? '...' : stats?.total_users || 0}</p>
                  </div>
                  {cardStyle !== 'minimal' && (
                    <div className={`rounded-sm bg-yellow-500/10 flex items-center justify-center ${cardStyle === 'compact' ? 'w-10 h-10' : 'w-12 h-12'}`}>
                      <Users className={`text-yellow-500 ${cardStyle === 'compact' ? 'w-5 h-5' : 'w-6 h-6'}`} />
                    </div>
                  )}
                </div>
              </div>

              <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm card-hover ${cardStyle === 'compact' ? 'p-4' : cardStyle === 'minimal' ? 'p-3 border-0 bg-white/[0.03]' : 'p-6'}`} data-testid="stat-logins-today">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-mono uppercase tracking-widest text-white/40 mb-1 ${cardStyle === 'compact' ? 'text-[10px]' : 'text-xs'}`}>Logins Today</p>
                    <p className={`font-bold text-white ${cardStyle === 'compact' ? 'text-2xl' : cardStyle === 'minimal' ? 'text-xl' : 'text-3xl'}`}>{loading ? '...' : stats?.total_logins_today || 0}</p>
                  </div>
                  {cardStyle !== 'minimal' && (
                    <div className={`rounded-sm bg-green-500/10 flex items-center justify-center ${cardStyle === 'compact' ? 'w-10 h-10' : 'w-12 h-12'}`}>
                      <TrendingUp className={`text-green-500 ${cardStyle === 'compact' ? 'w-5 h-5' : 'w-6 h-6'}`} />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        )}

        {/* Quick Actions & Recent Activity */}
        <div className={`grid gap-6 ${showQuickActions && showRecentActivity ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
          {/* Quick Actions */}
          {showQuickActions && (
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm p-6" data-testid="quick-actions-section">
            <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/documents">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 border-white/10 hover:bg-white/5 hover:border-primary/50">
                  <Upload className="w-6 h-6 text-primary" />
                  <span className="text-white text-sm">Upload Document</span>
                </Button>
              </Link>
              <Link to="/shared">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 border-white/10 hover:bg-white/5 hover:border-secondary/50">
                  <Share2 className="w-6 h-6 text-secondary" />
                  <span className="text-white text-sm">View Shared</span>
                </Button>
              </Link>
              <Link to="/documents">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 border-white/10 hover:bg-white/5 hover:border-yellow-500/50">
                  <FolderLock className="w-6 h-6 text-yellow-500" />
                  <span className="text-white text-sm">My Documents</span>
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 border-white/10 hover:bg-white/5 hover:border-white/50">
                  <Settings className="w-6 h-6 text-white/60" />
                  <span className="text-white text-sm">Settings</span>
                </Button>
              </Link>
            </div>
          </div>
          )}

          {/* Recent Activity */}
          {showRecentActivity && (
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm p-6" data-testid="recent-activity-section">
            <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Recent Activity
            </h3>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-sm" />
                    <div className="flex-1 h-4 bg-white/10 rounded" />
                  </div>
                ))}
              </div>
            ) : stats?.recent_activity?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_activity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-sm bg-white/5">
                    <span className="text-lg">{getActionIcon(activity.action)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm capitalize">
                        {activity.action.replace(/_/g, ' ')}
                      </p>
                      <p className="text-white/40 text-xs truncate">{typeof activity.details === 'object' ? JSON.stringify(activity.details) : activity.details}</p>
                      <p className="text-white/30 text-xs mt-1 font-mono">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/40 text-center py-8">No recent activity</p>
            )}
          </div>
          )}
        </div>

        {/* Security Status */}
        <div className="bg-black/40 backdrop-blur-xl border border-primary/30 rounded-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center animate-glow-pulse">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Security Status: <span className="text-primary">Protected</span>
              </h3>
              <p className="text-white/60 text-sm">
                All your documents are encrypted with AES-256. Your personal encryption key is active.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export { DashboardLayout };
export default Dashboard;
